/**
 * CL-ENGINEER-LAUNCH-002 — Canonical person identity for admin selection surfaces (pure).
 *
 * Architecture today intentionally keeps:
 * - users/{crmContactId} CRM contact
 * - users/{authUid} Auth runtime profile (created on signup)
 * linked via authUid / linkedCrmContactId.
 *
 * Admin pickers must show one selectable person, preferring the CRM contact id for writes.
 */

import { normalizeInviteEmail, isLinkablePortalUser, isArchivedPortalUser } from "./portalInvite";

function safeStr(value) {
  return String(value ?? "").trim();
}

function normEmail(value) {
  return normalizeInviteEmail(value);
}

export const PORTAL_RECORD_KIND = Object.freeze({
  CRM_CONTACT: "crm_contact",
  AUTH_RUNTIME_PROFILE: "auth_runtime_profile",
  UNKNOWN: "unknown",
});

/**
 * Classify a users/ document as CRM contact vs Auth-path runtime profile.
 */
export function classifyPortalUserRecordKind(row = {}) {
  const id = safeStr(row?.id || row?.uid);
  const authUid = safeStr(row?.authUid);
  const linkedCrm = safeStr(row?.linkedCrmContactId);
  const explicit = safeStr(row?.portalRecordKind).toLowerCase();

  if (explicit === PORTAL_RECORD_KIND.AUTH_RUNTIME_PROFILE || explicit === "auth_profile") {
    return PORTAL_RECORD_KIND.AUTH_RUNTIME_PROFILE;
  }
  if (explicit === PORTAL_RECORD_KIND.CRM_CONTACT || explicit === "crm") {
    return PORTAL_RECORD_KIND.CRM_CONTACT;
  }

  // Auth-path: document id is the Firebase Auth uid.
  if (authUid && id && id === authUid) {
    return PORTAL_RECORD_KIND.AUTH_RUNTIME_PROFILE;
  }
  // Auth profile often carries linkedCrmContactId pointing at CRM.
  if (linkedCrm && id && linkedCrm !== id) {
    return PORTAL_RECORD_KIND.AUTH_RUNTIME_PROFILE;
  }
  // CRM contacts: id is not Auth uid (or authUid empty / different).
  if (id && (!authUid || authUid !== id)) {
    return PORTAL_RECORD_KIND.CRM_CONTACT;
  }
  return PORTAL_RECORD_KIND.UNKNOWN;
}

export function isAuthRuntimePortalProfile(row = {}) {
  return classifyPortalUserRecordKind(row) === PORTAL_RECORD_KIND.AUTH_RUNTIME_PROFILE;
}

export function isCrmContactPortalRecord(row = {}) {
  return classifyPortalUserRecordKind(row) === PORTAL_RECORD_KIND.CRM_CONTACT;
}

function richnessScore(row = {}) {
  let score = 0;
  if (safeStr(row?.firstName) || safeStr(row?.lastName)) score += 3;
  if (safeStr(row?.displayName) || safeStr(row?.name)) score += 2;
  if (safeStr(row?.company)) score += 1;
  if (safeStr(row?.phone)) score += 1;
  if (Array.isArray(row?.assignedProjectIds) && row.assignedProjectIds.length) score += 2;
  if (Array.isArray(row?.services) && row.services.length) score += 1;
  if (row?.isTestContact === true) score += 0; // neutral
  if (safeStr(row?.notes) || safeStr(row?.adminNotes)) score += 1;
  return score;
}

function recordsShareExplicitLink(a = {}, b = {}) {
  const aId = safeStr(a?.id);
  const bId = safeStr(b?.id);
  const aAuth = safeStr(a?.authUid);
  const bAuth = safeStr(b?.authUid);
  const aLink = safeStr(a?.linkedCrmContactId);
  const bLink = safeStr(b?.linkedCrmContactId);
  if (aId && bAuth && aId === bAuth) return true;
  if (bId && aAuth && bId === aAuth) return true;
  if (aLink && aLink === bId) return true;
  if (bLink && bLink === aId) return true;
  return false;
}

/**
 * Resolve one canonical contact identity from records sharing a normalized email.
 */
function isSupersededIdentityRow(row = {}) {
  const status = safeStr(row?.recordStatus || "").toLowerCase();
  if (status === "superseded") return true;
  return Boolean(safeStr(row?.supersededByContactId));
}

export function resolveCanonicalContactIdentity(recordsForEmail = []) {
  const records = (Array.isArray(recordsForEmail) ? recordsForEmail : []).filter(
    (r) => safeStr(r?.id) && !isArchivedPortalUser(r) && !isSupersededIdentityRow(r)
  );

  if (!records.length) {
    return {
      canonicalRecord: null,
      linkedAuthProfile: null,
      duplicateRecords: [],
      resolutionReason: "no_records",
      hasIdentityConflict: false,
      requiresManualReview: false,
    };
  }

  if (records.length === 1) {
    const only = records[0];
    const kind = classifyPortalUserRecordKind(only);
    return {
      canonicalRecord: only,
      linkedAuthProfile: kind === PORTAL_RECORD_KIND.AUTH_RUNTIME_PROFILE ? only : null,
      duplicateRecords: [],
      resolutionReason: kind === PORTAL_RECORD_KIND.AUTH_RUNTIME_PROFILE ? "single_auth_profile" : "single_crm_contact",
      hasIdentityConflict: false,
      requiresManualReview: false,
    };
  }

  const crmRecords = records.filter((r) => isCrmContactPortalRecord(r));
  const authRecords = records.filter((r) => isAuthRuntimePortalProfile(r));

  // Explicit CRM ↔ Auth link pair.
  for (const crm of crmRecords) {
    const crmId = safeStr(crm.id);
    const crmAuth = safeStr(crm.authUid);
    const linkedAuth =
      authRecords.find((a) => safeStr(a.id) === crmAuth) ||
      authRecords.find((a) => safeStr(a.linkedCrmContactId) === crmId) ||
      null;
    if (linkedAuth && recordsShareExplicitLink(crm, linkedAuth)) {
      const extras = records.filter((r) => safeStr(r.id) !== crmId && safeStr(r.id) !== safeStr(linkedAuth.id));
      if (extras.length) {
        return {
          canonicalRecord: crm,
          linkedAuthProfile: linkedAuth,
          duplicateRecords: extras,
          resolutionReason: "crm_auth_linked_with_extra_duplicates",
          hasIdentityConflict: true,
          requiresManualReview: true,
        };
      }
      return {
        canonicalRecord: crm,
        linkedAuthProfile: linkedAuth,
        duplicateRecords: [linkedAuth],
        resolutionReason: "crm_auth_explicit_link",
        hasIdentityConflict: false,
        requiresManualReview: false,
      };
    }
  }

  // One CRM + one Auth without explicit link but same email — prefer CRM, flag review if Auth has different authUid story.
  if (crmRecords.length === 1 && authRecords.length === 1) {
    const crm = crmRecords[0];
    const auth = authRecords[0];
    const conflict =
      Boolean(safeStr(crm.authUid)) &&
      safeStr(crm.authUid) !== safeStr(auth.id) &&
      safeStr(crm.authUid) !== safeStr(auth.authUid);
    return {
      canonicalRecord: crm,
      linkedAuthProfile: auth,
      duplicateRecords: [auth],
      resolutionReason: conflict ? "email_match_auth_uid_conflict" : "email_match_prefer_crm",
      hasIdentityConflict: conflict,
      requiresManualReview: conflict,
    };
  }

  // Multiple CRM contacts same email — never auto-merge.
  if (crmRecords.length > 1) {
    const richest = [...crmRecords].sort((a, b) => richnessScore(b) - richnessScore(a))[0];
    return {
      canonicalRecord: richest,
      linkedAuthProfile: authRecords[0] || null,
      duplicateRecords: records.filter((r) => safeStr(r.id) !== safeStr(richest.id)),
      resolutionReason: "duplicate_crm_contacts_manual_review",
      hasIdentityConflict: true,
      requiresManualReview: true,
    };
  }

  // Only Auth profiles — prefer one with linkedCrmContactId, else richest.
  if (!crmRecords.length && authRecords.length) {
    const withLink = authRecords.find((a) => safeStr(a.linkedCrmContactId));
    const pick = withLink || [...authRecords].sort((a, b) => richnessScore(b) - richnessScore(a))[0];
    return {
      canonicalRecord: pick,
      linkedAuthProfile: pick,
      duplicateRecords: authRecords.filter((r) => safeStr(r.id) !== safeStr(pick.id)),
      resolutionReason: withLink ? "auth_profile_with_crm_link" : "auth_profiles_only",
      hasIdentityConflict: authRecords.length > 1,
      requiresManualReview: authRecords.length > 1,
    };
  }

  const richest = [...records].sort((a, b) => richnessScore(b) - richnessScore(a))[0];
  return {
    canonicalRecord: richest,
    linkedAuthProfile: authRecords[0] || null,
    duplicateRecords: records.filter((r) => safeStr(r.id) !== safeStr(richest.id)),
    resolutionReason: "deterministic_richness_fallback",
    hasIdentityConflict: records.length > 1,
    requiresManualReview: true,
  };
}

function displayNameOf(row = {}) {
  const composed = `${safeStr(row?.firstName)} ${safeStr(row?.lastName)}`.trim();
  return composed || safeStr(row?.displayName) || safeStr(row?.name) || "";
}

/**
 * Options for collaborator User account dropdown — one row per canonical person.
 */
export function buildCanonicalCollaboratorPickerOptions(users = [], { projectRoleHint = "" } = {}) {
  const linkable = (Array.isArray(users) ? users : []).filter(
    (u) => isLinkablePortalUser(u) && !isSupersededIdentityRow(u)
  );
  const byEmail = new Map();
  linkable.forEach((row) => {
    const em = normEmail(row?.email || row?.emailLower);
    if (!em) return;
    if (!byEmail.has(em)) byEmail.set(em, []);
    byEmail.get(em).push(row);
  });

  const options = [];
  byEmail.forEach((group, emailLower) => {
    const resolved = resolveCanonicalContactIdentity(group);
    if (resolved.requiresManualReview && resolved.hasIdentityConflict && !resolved.canonicalRecord) {
      options.push({
        id: `conflict:${emailLower}`,
        value: "",
        emailLower,
        label: `${emailLower} — Identity review required`,
        disabled: true,
        isConflict: true,
        canonicalId: "",
        resolution: resolved,
      });
      return;
    }
    const canonical = resolved.canonicalRecord;
    if (!canonical) return;
    const name = displayNameOf(canonical);
    const portalLinked = Boolean(
      safeStr(canonical.authUid) ||
        (resolved.linkedAuthProfile && safeStr(resolved.linkedAuthProfile.id))
    );
    const roleBit = safeStr(projectRoleHint);
    const parts = [emailLower];
    if (name) parts.push(`— ${name}`);
    const tags = [];
    if (roleBit) tags.push(roleBit);
    if (portalLinked) tags.push("Portal account linked");
    else if (isCrmContactPortalRecord(canonical) && !safeStr(canonical.authUid)) tags.push("CRM only");
    if (resolved.requiresManualReview) tags.push("Identity review required");
    const tagStr = tags.length ? ` · ${tags.join(" · ")}` : "";
    options.push({
      id: safeStr(canonical.id),
      value: safeStr(canonical.id),
      emailLower,
      label: `${parts.join(" ")}${tagStr}`,
      disabled: false,
      isConflict: Boolean(resolved.requiresManualReview && resolved.hasIdentityConflict),
      canonicalId: safeStr(canonical.id),
      linkedAuthUid: safeStr(canonical.authUid) || safeStr(resolved.linkedAuthProfile?.id) || "",
      resolution: resolved,
      record: canonical,
    });
  });

  return options.sort((a, b) => a.emailLower.localeCompare(b.emailLower));
}

/**
 * Read-only duplicate-email diagnostic for admin tools.
 */
export function buildDuplicateEmailIdentityDiagnostic(users = []) {
  const byEmail = new Map();
  (Array.isArray(users) ? users : []).forEach((row) => {
    if (isArchivedPortalUser(row)) return;
    const em = normEmail(row?.email || row?.emailLower);
    if (!em) return;
    if (!byEmail.has(em)) byEmail.set(em, []);
    byEmail.get(em).push(row);
  });

  const rows = [];
  byEmail.forEach((group, emailLower) => {
    if (group.length < 2) return;
    const resolved = resolveCanonicalContactIdentity(group);
    rows.push({
      emailLower,
      recordCount: group.length,
      recordIds: group.map((r) => safeStr(r.id)),
      authUidLinks: group.map((r) => safeStr(r.authUid) || null),
      linkedCrmContactIds: group.map((r) => safeStr(r.linkedCrmContactId) || null),
      kinds: group.map((r) => classifyPortalUserRecordKind(r)),
      recommendedCanonicalId: safeStr(resolved.canonicalRecord?.id) || null,
      resolutionReason: resolved.resolutionReason,
      hasIdentityConflict: resolved.hasIdentityConflict,
      requiresManualReview: resolved.requiresManualReview,
      safeToAutoResolve: !resolved.requiresManualReview && Boolean(resolved.canonicalRecord),
      reason: resolved.requiresManualReview
        ? "Unresolved identity conflict — do not auto-merge"
        : "Linked CRM + Auth profile can be treated as one person in pickers",
      projectIds: [
        ...new Set(
          group.flatMap((r) => (Array.isArray(r.assignedProjectIds) ? r.assignedProjectIds.map(safeStr) : []))
        ),
      ],
    });
  });

  return {
    duplicateEmailCount: rows.length,
    rows: rows.sort((a, b) => a.emailLower.localeCompare(b.emailLower)),
  };
}

/**
 * Gate for engineer invite send when collaborator identity is ambiguous.
 */
export function buildEngineerIdentityInviteGate({
  row = {},
  userRows = [],
  inviteContactId = "",
  inviteEmail = "",
} = {}) {
  const email = normEmail(row?.email || row?.emailLower || inviteEmail);
  const selectedId = safeStr(row?.uid || row?.userId || row?.contactId);
  const group = (Array.isArray(userRows) ? userRows : []).filter(
    (u) => normEmail(u?.email || u?.emailLower) === email
  );
  const resolved = resolveCanonicalContactIdentity(group);
  const warnings = [];
  let blockSend = false;
  let blockReason = "";

  if (!email) {
    return {
      ok: false,
      blockSend: true,
      blockReason: "Add the correct recipient email before inviting.",
      warnings: ["missing_email"],
      resolution: resolved,
    };
  }

  if (group.length > 1 && resolved.requiresManualReview) {
    blockSend = true;
    blockReason =
      "Identity review required — multiple unresolved records share this email. Confirm the canonical contact before sending.";
    warnings.push("unresolved_duplicate_identity");
  }

  if (selectedId && resolved.canonicalRecord && selectedId !== safeStr(resolved.canonicalRecord.id)) {
    const selected = group.find((u) => safeStr(u.id) === selectedId);
    if (selected && isAuthRuntimePortalProfile(selected) && !safeStr(selected.linkedCrmContactId)) {
      blockSend = true;
      blockReason =
        "Selected record looks like a secondary Auth profile without an explicit CRM link. Choose the canonical contact.";
      warnings.push("selected_secondary_auth_profile");
    } else if (resolved.requiresManualReview) {
      warnings.push("selected_id_differs_from_recommended_canonical");
    }
  }

  const inviteCid = safeStr(inviteContactId);
  if (inviteCid && selectedId && inviteCid !== selectedId) {
    warnings.push("invite_contact_differs_from_collaborator_uid");
    blockSend = true;
    blockReason =
      blockReason ||
      "Invitation contact id and project collaborator record do not match. Fix identity before sending.";
  }

  const inviteEm = normEmail(inviteEmail);
  if (inviteEm && email && inviteEm !== email) {
    blockSend = true;
    blockReason = "Invited email differs from the canonical contact email.";
    warnings.push("invite_email_mismatch");
  }

  // CRM-only pre-signup remains valid.
  if (!group.length && email) {
    return {
      ok: true,
      blockSend: false,
      blockReason: "",
      warnings: ["crm_only_or_manual_email"],
      resolution: resolved,
    };
  }

  return {
    ok: !blockSend,
    blockSend,
    blockReason,
    warnings,
    resolution: resolved,
    canonicalId: safeStr(resolved.canonicalRecord?.id) || selectedId || "",
  };
}

/**
 * Fields for users/{authUid} create — mark as runtime Auth profile, not a second CRM person.
 */
export function buildAuthRuntimeProfileCreateFields({
  authUid = "",
  firstName = "",
  lastName = "",
  crmContact = null,
} = {}) {
  const preferred = preferCrmNamesForAuthProfileWrite({
    signupFirstName: firstName,
    signupLastName: lastName,
    crmContact,
  });
  const fn = preferred.firstName;
  const ln = preferred.lastName;
  return {
    portalRecordKind: PORTAL_RECORD_KIND.AUTH_RUNTIME_PROFILE,
    authUid: safeStr(authUid),
    firstName: fn || null,
    lastName: ln || null,
    displayName: `${fn} ${ln}`.trim() || null,
    // Auth-path profile is not a second CRM person.
    isIndependentCrmContact: false,
  };
}

/**
 * Client Record heading subtitle — roles when deterministically supported.
 */
export function buildClientRecordIdentityHeadline({
  profile = null,
  authProfile = null,
  userId = "",
} = {}) {
  const id = safeStr(userId || profile?.id);
  const records = [];
  if (profile && id) records.push({ ...profile, id });
  if (authProfile && safeStr(authProfile.id) && safeStr(authProfile.id) !== id) {
    records.push(authProfile);
  }
  const email = normEmail(profile?.email || profile?.emailLower);
  const sameEmailRecords = records.filter(
    (r) => !email || normEmail(r?.email || r?.emailLower) === email || !normEmail(r?.email || r?.emailLower)
  );
  const resolution =
    sameEmailRecords.length > 1 ? resolveCanonicalContactIdentity(sameEmailRecords) : null;

  const roles = [];
  const constructionRoles = Array.isArray(profile?.constructionRoles)
    ? profile.constructionRoles.map((r) => safeStr(r)).filter(Boolean)
    : [];
  constructionRoles.forEach((r) => {
    const label = r.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    if (!roles.includes(label)) roles.push(label);
  });
  const primary = safeStr(profile?.primaryUserType || profile?.role).toLowerCase();
  if (primary && primary !== "client" && primary !== "user") {
    const label = primary.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    if (!roles.includes(label)) roles.push(label);
  }

  let roleLine = "";
  if (roles.length === 1) {
    roleLine = roles[0];
  } else if (roles.length > 1) {
    roleLine = `Project roles: ${roles.join(", ")}`;
  }

  const tags = [];
  if (hasConstructionContactSignal(profile)) tags.push("Construction contact");
  const portalLinked = Boolean(
    safeStr(profile?.authUid) || (authProfile && safeStr(authProfile.id))
  );
  if (portalLinked) tags.push("Portal account linked");
  else if (isCrmContactPortalRecord({ ...profile, id }) && !safeStr(profile?.authUid)) {
    tags.push("CRM only");
  }

  const identityReviewRequired = Boolean(resolution?.requiresManualReview);
  return {
    roleLine,
    tags,
    subtitle: [roleLine, ...tags].filter(Boolean).join(" · "),
    identityReviewRequired,
    identityReviewMessage: identityReviewRequired
      ? "Identity review required — multiple records are linked to this email."
      : "",
    resolution,
  };
}

function hasConstructionContactSignal(profile = {}) {
  const services = Array.isArray(profile?.services) ? profile.services : [];
  if (services.includes("construction")) return true;
  const clientType = safeStr(profile?.clientType).toLowerCase();
  return clientType === "construction" || clientType === "both" || profile?.portalEnabled === true;
}

/**
 * Whether an invite doc has had its invitation email sent (audit fields).
 */
export function inviteDocEmailWasSent(invite = null) {
  if (!invite || typeof invite !== "object") return false;
  if (invite.emailSentAt != null) return true;
  const status = safeStr(invite.sendStatus || invite.emailSendStatus).toLowerCase();
  return status === "sent" || status === "delivered";
}

/**
 * Prefer CRM values when signup would write blank over richer CRM contact data.
 * Does not mutate CRM; returns Auth-profile-safe name fields only.
 */
export function preferCrmNamesForAuthProfileWrite({
  signupFirstName = "",
  signupLastName = "",
  crmContact = null,
} = {}) {
  const sFirst = safeStr(signupFirstName);
  const sLast = safeStr(signupLastName);
  const cFirst = safeStr(crmContact?.firstName);
  const cLast = safeStr(crmContact?.lastName);
  return {
    firstName: sFirst || cFirst,
    lastName: sLast || cLast,
    usedCrmFallback: Boolean((!sFirst && cFirst) || (!sLast && cLast)),
  };
}
